import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../services/api';

/**
 * Lets Pulse-SRE finish logging in a user who is already signed into Pulse,
 * without asking for a password a second time.
 *
 * Pulse-SRE's middleware redirects an unauthenticated visitor here with
 * `?next=<path within Pulse-SRE>`. If the user isn't logged into Pulse
 * either, we bounce them to Pulse's own /login (which, via `getPostLoginRedirect`
 * in AuthContext, returns here once they do log in). Once logged in, we mint
 * a short-lived one-time code (POST /auth/sso/sre/mint) and hand the browser
 * off to Pulse-SRE's own callback, which exchanges it server-to-server.
 */
const SsoHandoff: React.FC = () => {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const mintStarted = useRef(false);

  const rawNext = searchParams.get('next') || '/';
  // Defense in depth: this value also gets re-validated on the Pulse-SRE
  // side before it's used in a redirect there.
  const safeNext = rawNext.startsWith('/') && !rawNext.startsWith('//') && !rawNext.includes('://')
    ? rawNext
    : '/';

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!user) {
      const here = `/sso/handoff?next=${encodeURIComponent(safeNext)}`;
      window.location.replace(`/login?next=${encodeURIComponent(here)}`);
      return;
    }

    const dashboardUrl = process.env.REACT_APP_SRE_DASHBOARD_URL;
    if (!dashboardUrl) {
      setError('Pulse-SRE is not configured (REACT_APP_SRE_DASHBOARD_URL is unset).');
      return;
    }

    if (mintStarted.current) {
      return;
    }
    mintStarted.current = true;

    api
      .post('/auth/sso/sre/mint')
      .then((response) => {
        const code = response.data?.code;
        if (!code) {
          setError('Unexpected response while starting Pulse-SRE sign-in.');
          return;
        }
        const callback = `${dashboardUrl.replace(/\/$/, '')}/api/auth/sso-callback?code=${encodeURIComponent(code)}&next=${encodeURIComponent(safeNext)}`;
        window.location.replace(callback);
      })
      .catch((err) => {
        if (err?.response?.status === 403) {
          setError('You are not authorized to access Pulse-SRE. Contact your administrator if you believe this is a mistake.');
        } else {
          setError('Unable to start Pulse-SRE sign-in right now. Please try again shortly.');
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <p className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-md p-4">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
        <p className="text-sm text-gray-600">Signing you into Pulse-SRE…</p>
      </div>
    </div>
  );
};

export default SsoHandoff;
